// SidebarButton.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';

// CSS
import './SidebarButton.css';

// Icons
import Icon from 'svg/Icon';

/**
 * SidebarButton
 * 
 * Renders a button in the sidebar.
 * 
 * @param {string} iconKey - The key for the icon to display.
 * @param {string} [iconPath] - Optional SVG path fallback when iconKey is not in the catalog.
 * @param {string} [iconResource] - Optional image URL/import; overrides iconKey.
 * @param {string} label - The label to display on the button.
 * @param {string} [description] - Optional muted subtext under the label.
 * @param {number} [percentComplete] - Optional 0–100 workflow completion. Shows a green progress bar under the label when provided.
 * @param {string} [title] - Optional native tooltip; defaults to label in compact rail.
 * @param {ReactNode} rightContent - Optional right-side content to display.
 * @param {boolean} enabled - Whether the button is interactive.
 * @param {boolean} selected - Whether the button represents the active destination.
 * @param {function} onClick - Handler for when the button is clicked.
 *  
 *
 * @returns {ReactNode} - SidebarButton.
 */
const SidebarButton = React.memo(({
  iconKey,
  iconPath,
  iconResource,
  label,
  description = '',
  percentComplete,
  title,
  rightContent,
  enabled = true,
  selected = false,
  onClick,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();
    const { isSidebarRailCompact } = useUI();

    // ----- STATE ----- //

    // Hover State

    const [hovered, setHovered] = useState(false);
    const [rightHovered, setRightHovered] = useState(false);

    // ----- HANDLERS ----- //

    /**
     * Handles click interaction when the button is enabled.
     */

    const handleClick = () => {
        if (!enabled) return;
        onClick && onClick();
    };

    const showSelected = selected && enabled;
    const showHover = hovered && enabled && !showSelected;
    const descriptionText = String(description || '').trim();
    const compact = Boolean(isSidebarRailCompact);
    const iconSize = compact ? 22 : 18;
    const progressValue = percentComplete == null ? null : Number(percentComplete);
    const hasProgress = !compact && Number.isFinite(progressValue);
    const clampedProgress = hasProgress
        ? Math.max(0, Math.min(100, progressValue))
        : 0;

    const backgroundColor = showSelected
        ? theme.backgroundSelected
        : (showHover ? theme.backgroundHover : 'transparent');

    const foregroundColor = showSelected
        ? theme.foregroundSelected
        : theme.foreground;

    const iconColor = showSelected
        ? theme.foregroundSelected
        : (showHover ? theme.foreground : theme.foregroundMuted);

    const descriptionColor = showSelected
        ? theme.foregroundSelected
        : theme.foregroundMuted;

    // ----- RENDER ----- //

    return (

        <div
          className={[
            'sidebar-button-container',
            descriptionText && !compact ? 'has-description' : '',
            hasProgress ? 'has-progress' : '',
            compact ? 'is-rail-compact' : '',
          ].filter(Boolean).join(' ')}
          style={{
                backgroundColor,
                color: foregroundColor,
                cursor: enabled ? 'pointer' : 'not-allowed',
                opacity: enabled ? 1 : 0.45,
            }}
          title={title || (compact ? (label || '') : undefined)}
          onClick={handleClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => {
                setHovered(false);
                setRightHovered(false);
            }}
        >
            {/* ICON */}
            <div className="sidebar-button-icon">
                {iconResource ? (
                    <img
                      alt=""
                      className="sidebar-button-icon-resource"
                      draggable={false}
                      src={iconResource}
                      style={{
                        height: iconSize,
                        width: iconSize,
                      }}
                    />
                ) : (
                    <Icon
                      iconKey={iconKey}
                      path={iconPath}
                      width={iconSize}
                      height={iconSize}
                      color={iconColor}
                    />
                )}
            </div>

            {/* LABEL + OPTIONAL DESCRIPTION */}
            {!compact ? (
                <div className="sidebar-button-text">
                    <div className="sidebar-button-label">
                        {label}
                    </div>
                    {descriptionText ? (
                        <div
                          className="sidebar-button-description"
                          style={{ color: descriptionColor }}
                        >
                            {descriptionText}
                        </div>
                    ) : null}

                    {/* PROGRESS */}
                    {hasProgress ? (
                        <div
                          className="sidebar-button-progress"
                          aria-label={`${Math.round(clampedProgress)}% complete`}
                          aria-valuemax={100}
                          aria-valuemin={0}
                          aria-valuenow={Math.round(clampedProgress)}
                          role="progressbar"
                        >
                            <div
                              className="sidebar-button-progress-fill"
                              style={{
                                    width: `${clampedProgress}%`,
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* RIGHT */}
            {!compact && (rightContent || showSelected) ? (
                <div
                  className="sidebar-button-right"
                  style={{
                        alignItems: 'center',
                        backgroundColor: rightHovered && rightContent ? theme.backgroundHover : 'transparent',
                        display: 'flex',
                        gap: 6,
                    }}
                  onMouseEnter={rightContent ? () => setRightHovered(true) : undefined}
                  onMouseLeave={rightContent ? () => setRightHovered(false) : undefined}
                >
                    {rightContent}

                    {showSelected && (
                        <Icon
                          iconKey="chevron_right"
                          width={16}
                          height={16}
                          color={iconColor}
                        />
                    )}
                </div>
            ) : null}
        </div>
    );

});

// Dev Tools
SidebarButton.displayName = 'SidebarButton';

export default SidebarButton;
